// 飞书风格整体布局：顶部细条（产品名 + 用户区）+ 左侧分组导航 + 内容区。
// 导航数据在 ./nav.ts（按 capabilities 过滤，与服务端鉴权共用 shared/capabilities.ts 枚举）；
// 移动端为抽屉式导航，触控目标 ≥44px。路由表见 src/App.tsx。

import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Avatar } from "@bf/ui";
import { CAPABILITY_LABELS } from "../../../shared/capabilities";
import { useAuth } from "../../lib/auth";
import { NAV_GROUPS, type NavIconKey } from "./nav";

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const navIcons: Record<NavIconKey, ReactNode> = {
  home: (
    <svg {...iconProps}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /><path d="M9 21v-6h6v6" /></svg>
  ),
  doc: (
    <svg {...iconProps}><path d="M6 2h9l5 5v15H6z" /><path d="M14 2v6h6" /><path d="M9 13h6M9 17h6" /></svg>
  ),
  check: (
    <svg {...iconProps}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="m8 12 3 3 6-6" /></svg>
  ),
  users: (
    <svg {...iconProps}><circle cx="9" cy="8" r="3.5" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M17.5 14.4c2.3.7 3.5 2.7 3.5 5.6" /></svg>
  ),
  flag: (
    <svg {...iconProps}><path d="M5 21V4" /><path d="M5 4c4-2 8 2 14 0v9c-6 2-10-2-14 0" /></svg>
  ),
  bulb: (
    <svg {...iconProps}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-4 10.5c.8.7 1 1.5 1 2.5h6c0-1 .2-1.8 1-2.5A6 6 0 0 0 12 3z" /></svg>
  ),
  trophy: (
    <svg {...iconProps}><path d="M8 21h8M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M7 6H4a3 3 0 0 0 3 5M17 6h3a3 3 0 0 1-3 5" /></svg>
  ),
  medal: (
    <svg {...iconProps}><circle cx="12" cy="15" r="5" /><path d="m12 13 1 2 2 .2-1.5 1.4.4 2.1-1.9-1-1.9 1 .4-2.1L9 15.2 11 15z" /><path d="M8.5 10.5 5 3h5l2 4 2-4h5l-3.5 7.5" /></svg>
  ),
  star: (
    <svg {...iconProps}><path d="m12 3 2.7 5.6 6.1.8-4.5 4.3 1.1 6.1L12 17l-5.4 2.8 1.1-6.1L3.2 9.4l6.1-.8z" /></svg>
  ),
  chat: (
    <svg {...iconProps}><path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z" /></svg>
  ),
  warn: (
    <svg {...iconProps}><path d="M12 3 2 21h20z" /><path d="M12 10v4M12 17.5v.5" /></svg>
  ),
  swap: (
    <svg {...iconProps}><path d="M4 7h13l-3-3M20 17H7l3 3" /></svg>
  ),
  book: (
    <svg {...iconProps}><path d="M4 4a2 2 0 0 1 2-2h14v18H6a2 2 0 0 0-2 2z" /><path d="M4 19a2 2 0 0 1 2-2h14" /></svg>
  ),
  horn: (
    <svg {...iconProps}><path d="M3 10v4h3l7 5V5l-7 5z" /><path d="M17 8a5 5 0 0 1 0 8" /></svg>
  ),
  rank: (
    <svg {...iconProps}><path d="M8 21V10M16 21V3M3 21h18M12 21v-6" /></svg>
  ),
  coins: (
    <svg {...iconProps}><circle cx="9" cy="9" r="6" /><path d="M14.5 5.6a6 6 0 1 1-8.9 8.9" /></svg>
  ),
  gear: (
    <svg {...iconProps}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" /></svg>
  ),
  pulse: (
    <svg {...iconProps}><path d="M3 12h4l2-6 4 12 2-6h6" /></svg>
  ),
  help: (
    <svg {...iconProps}><path d="M4 4a2 2 0 0 1 2-2h14v18H6a2 2 0 0 0-2 2z" /><path d="M4 19a2 2 0 0 1 2-2h14" /></svg>
  ),
  user: (
    <svg {...iconProps}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" /></svg>
  ),
  key: (
    <svg {...iconProps}><circle cx="8" cy="15" r="4" /><path d="m11 12 8-8M16 7l3 3M14 9l2 2" /></svg>
  ),
};

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { hasCapability } = useAuth();
  return (
    <nav className="flex flex-col gap-4 px-3 py-4">
      {NAV_GROUPS.map((group) => {
        const visible = group.items.filter((item) => hasCapability(...item.anyOf));
        if (visible.length === 0) return null;
        return (
          <div key={group.title}>
            <p className="px-2 pb-1.5 text-xs text-[var(--color-faint)]">{group.title}</p>
            <ul className="flex flex-col gap-0.5">
              {visible.map((item) => (
                <li key={item.to}>
                  {item.to === "/help" ? (
                    <a href="/help" onClick={onNavigate} className="flex min-h-[44px] items-center gap-2.5 rounded-[var(--radius-md)] px-2 text-sm text-[var(--color-muted)] transition-colors hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)] sm:h-9 sm:min-h-0">
                      <span className="shrink-0 opacity-80">{navIcons[item.icon]}</span>{item.label}
                    </a>
                  ) : (
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `flex min-h-[44px] items-center gap-2.5 rounded-md px-2 text-sm transition-colors sm:min-h-0 sm:h-9 ${
                          isActive
                            ? "bg-[var(--color-primary-soft)] font-medium text-[var(--color-primary)]"
                            : "text-[var(--color-muted)] hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)]"
                        }`
                      }
                    >
                      <span className="shrink-0 opacity-80">{navIcons[item.icon]}</span>{item.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout, capabilities } = useAuth();
  const primaryCapability = capabilities.find((capability) => capability !== "employee") ?? "employee";
  const roleLabel = CAPABILITY_LABELS[primaryCapability];

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center border-b border-[var(--color-line)] px-4">
        <span className="min-w-0">
          <strong className="block truncate text-sm text-[var(--color-ink)]">跃迁积分</strong>
          <small className="block truncate text-[11px] text-[var(--color-faint)]">内部积分激励管理系统</small>
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <NavItems onNavigate={onNavigate} />
      </div>
      {user ? (
        <div className="shrink-0 border-t border-[var(--color-line)] p-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <Avatar name={user.name} />
            <span className="min-w-0 flex-1">
              <strong className="block truncate text-sm text-[var(--color-ink)]">{user.name}</strong>
              <small className="block truncate text-xs text-[var(--color-faint)]">{roleLabel}</small>
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              className="min-h-9 shrink-0 rounded-[var(--radius-md)] px-2 text-xs text-[var(--color-faint)] hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)]"
            >
              退出
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AppLayout() {
  const { user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // 路由变化关闭抽屉
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-[var(--color-bg)]">
      {/* 桌面侧栏 */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 border-r border-[var(--color-line)] bg-[var(--color-panel)] md:block"><SidebarContent /></aside>

      {/* 移动端抽屉 */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-[var(--color-overlay)]" onClick={() => setDrawerOpen(false)} aria-hidden />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[var(--color-panel)] shadow-[var(--shadow-lg)]">
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 top-1.5 z-10 inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-muted)] hover:bg-[var(--color-soft)]"
            >
              <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            </button>
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col md:pl-56">
        <header className="fixed inset-x-0 top-0 z-20 flex h-14 items-center justify-between border-b border-[var(--color-line)] bg-[var(--color-panel)] px-3 md:hidden">
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setDrawerOpen(true)}
            className="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-muted)] hover:bg-[var(--color-soft)]"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <strong className="text-sm text-[var(--color-ink)]">跃迁积分</strong>
          {user ? <Avatar name={user.name} /> : <span className="size-8" />}
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 p-4 pt-[4.5rem] sm:p-6 sm:pt-[4.5rem] md:pt-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
